import { useState, useEffect, useRef } from 'react';
import { useToast } from '../components/Toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import { useGeolocation } from '../hooks/use-geolocation';
import { useGeofence } from '../hooks/use-geofence';
import { useLocationTracking } from '../hooks/use-location-tracking';
import MobileMap from '../components/maps/MobileMap';
import GeofenceCircle from '../components/maps/GeofenceCircle';
import NpsBottomSheet from '../components/NpsBottomSheet';
import KmSheet from '../components/journey/KmSheet';
import { useAuth } from '../stores/auth';
import { useAutoCheckout } from '../hooks/use-auto-checkout';
import {
  ArrowLeft,
  MapPin,
  Phone,
  LogIn,
  LogOut,
  Camera,
  ClipboardCheck,
  DollarSign,
  Star,
  Clock,
  CheckCircle,
  AlertCircle,
  Award,
  AlertTriangle,
  FileX,
  Navigation,
  Crosshair,
  Package,
  Target,
  RefreshCw,
  LayoutGrid,
  Boxes,
  ClipboardList,
} from 'lucide-react';

interface StoreInfo {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  contact_name?: string;
  contact_phone?: string;
  latitude?: number | null;
  longitude?: number | null;
  geofence_radius?: number | null;
}

interface ActiveVisit {
  id: string;
  store_id: string;
  check_in_time: string;
  check_in_lat?: number;
  check_in_lng?: number;
  status: 'IN_PROGRESS' | 'COMPLETED';
  perfect_store_score?: number;
}

function formatElapsed(startTime: string): string {
  const diff = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  return `${minutes}m`;
}

export default function Visit({ storeId }: { storeId: string }) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const geo = useGeolocation();
  const locationTracking = useLocationTracking();
  const { user } = useAuth();
  const [showNps, setShowNps] = useState(false);
  const [completedVisitId, setCompletedVisitId] = useState<string | null>(null);
  const [kmSheetOpen, setKmSheetOpen] = useState(false);
  const [kmSheetType, setKmSheetType] = useState<'start' | 'end'>('start');
  const [journeyId, setJourneyId] = useState<string | null>(null);

  // Geofence override modal state
  const [showGeofenceModal, setShowGeofenceModal] = useState(false);
  const [geofenceOverrideReason, setGeofenceOverrideReason] = useState('');
  const [geofenceError, setGeofenceError] = useState<{
    message: string;
    distance_meters: number;
    geofence_radius: number;
  } | null>(null);
  const pendingCoordsRef = useRef<{ latitude: number; longitude: number } | null>(null);

  // Visit timer
  const [elapsed, setElapsed] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch store information
  const { data: store, isLoading: isLoadingStore } = useQuery<StoreInfo>({
    queryKey: ['store', storeId],
    queryFn: () => api.get(`/stores/${storeId}`).then(res => res.data.data),
  });

  // Fetch active visit for this store
  const { data: activeVisit, isLoading: isLoadingVisit } = useQuery<ActiveVisit | null>({
    queryKey: ['visit', 'active', storeId],
    queryFn: () => api.get(`/visits/active/${storeId}`).then(res => res.data.data).catch(() => null),
  });

  // Fetch unpaired rupture photos for pending reposition
  const { data: unpairedPhotos = [] } = useQuery<{ id: string; type: string; url: string; created_at: string }[]>({
    queryKey: ['photos', 'unpaired', activeVisit?.id],
    queryFn: () => api.get(`/photos/visit/${activeVisit!.id}/unpaired`).then(res => res.data.data),
    enabled: !!activeVisit?.id && activeVisit?.status === 'IN_PROGRESS',
    refetchInterval: 15000, // refresh every 15s while visit active
  });

  const { data: npsSurvey } = useQuery({
    queryKey: ['nps-auto'],
    queryFn: () => api.get('/surveys/nps-auto').then(res => res.data.data),
    enabled: !!user,
  });

  const storeHasCoords = !!(store?.latitude && store?.longitude);
  const geofenceRadius = store?.geofence_radius ?? 500;

  const geofence = useGeofence({
    userLat: geo.latitude,
    userLng: geo.longitude,
    storeLat: store?.latitude ?? null,
    storeLng: store?.longitude ?? null,
    radiusMeters: geofenceRadius,
  });

  // Auto-checkout if promoter leaves geofence for 5+ minutes
  useAutoCheckout({
    visitId: activeVisit?.id ?? null,
    isActive: activeVisit?.status === 'IN_PROGRESS',
    storeLat: store?.latitude ? Number(store.latitude) : null,
    storeLng: store?.longitude ? Number(store.longitude) : null,
    radiusMeters: geofenceRadius,
    graceMinutes: 5,
    onBeforeCheckout: () => {
      locationTracking.stop();
    },
    onAutoCheckout: () => {
      queryClient.invalidateQueries({ queryKey: ['visit'] });
      queryClient.invalidateQueries({ queryKey: ['route'] });
      // Trigger NPS if survey configured (same as manual checkout)
      if (npsSurvey && activeVisit?.id) {
        setCompletedVisitId(activeVisit.id);
        setShowNps(true);
      } else {
        setLocation('/');
      }
    },
  });

  // Request GPS on mount
  useEffect(() => {
    if (storeHasCoords) {
      geo.getCurrentPosition();
    }
  }, [storeHasCoords]); // eslint-disable-line react-hooks/exhaustive-deps

  // Visit timer
  const isVisitActive = activeVisit?.status === 'IN_PROGRESS';

  useEffect(() => {
    if (isVisitActive && activeVisit?.check_in_time) {
      setElapsed(formatElapsed(activeVisit.check_in_time));
      timerRef.current = setInterval(() => {
        setElapsed(formatElapsed(activeVisit.check_in_time));
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isVisitActive, activeVisit?.check_in_time]);

  // Auto-resume tracking if page reloads during active visit
  useEffect(() => {
    if (isVisitActive && !locationTracking.isTracking) {
      locationTracking.start();
    }
  }, [isVisitActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check-in mutation
  const checkInMutation = useMutation({
    mutationFn: async (payload: {
      latitude: number;
      longitude: number;
      geofence_override?: boolean;
      geofence_override_reason?: string;
    }) => {
      const response = await api.post('/visits/check-in', {
        store_id: storeId,
        ...payload,
      });
      return response.data.data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['visit', 'active', storeId] });
      queryClient.invalidateQueries({ queryKey: ['visits', 'today'] });
      queryClient.invalidateQueries({ queryKey: ['route', 'today'] });
      setShowGeofenceModal(false);
      setGeofenceError(null);
      setGeofenceOverrideReason('');
      pendingCoordsRef.current = null;
      locationTracking.start();

      // Trigger KM sheet if journey needs km_start
      if (data?.journey?.needs_km_start && data?.journey?.id) {
        setJourneyId(data.journey.id);
        setKmSheetType('start');
        setKmSheetOpen(true);
      }
    },
    onError: (error: any) => {
      const errData = error.response?.data?.error;
      if (errData?.code === 'GEOFENCE_VIOLATION') {
        setGeofenceError({
          message: errData.message,
          distance_meters: errData.distance_meters,
          geofence_radius: errData.geofence_radius,
        });
        setShowGeofenceModal(true);
      } else {
        toast({
          title: 'Erro no check-in',
          description: errData?.message || 'Não foi possível fazer check-in. Tente novamente.',
          variant: 'error',
        });
      }
    },
  });

  // Check-out mutation
  const checkOutMutation = useMutation({
    mutationFn: async (coords?: { latitude?: number; longitude?: number }) => {
      if (!activeVisit) throw new Error('Nenhuma visita ativa');
      const payload: any = {};
      if (coords?.latitude && coords?.longitude) {
        payload.latitude = coords.latitude;
        payload.longitude = coords.longitude;
      }
      const response = await api.post(`/visits/${activeVisit.id}/check-out`, payload);
      return response.data.data;
    },
    onSuccess: (data: any) => {
      locationTracking.stop();
      queryClient.invalidateQueries({ queryKey: ['visit', 'active', storeId] });
      queryClient.invalidateQueries({ queryKey: ['visits', 'today'] });
      queryClient.invalidateQueries({ queryKey: ['route', 'today'] });

      // Trigger KM sheet if journey needs km_end
      if (data?.journey?.needs_km_end && data?.journey?.id) {
        setJourneyId(data.journey.id);
        setKmSheetType('end');
        setKmSheetOpen(true);
        // After KM is saved/skipped, NPS or redirect will happen via KmSheet onClose
        setCompletedVisitId(data.id || activeVisit?.id);
        return;
      }

      if (npsSurvey) {
        setCompletedVisitId(data.id || activeVisit?.id);
        setShowNps(true);
      } else {
        setLocation(`/visit-summary/${data.id || activeVisit?.id}`);
      }
    },
    onError: (error: any) => {
      toast({ title: error.response?.data?.error?.message || 'Erro ao fazer check-out', variant: 'error' });
    },
  });

  const handleCheckIn = async () => {
    const coords = await geo.getCurrentPosition();
    if (!coords) {
      toast({
        title: 'GPS indisponível',
        description: 'Não foi possível obter sua localização. Verifique se o GPS está habilitado.',
        variant: 'error',
      });
      return;
    }
    pendingCoordsRef.current = { latitude: coords.latitude, longitude: coords.longitude };
    checkInMutation.mutate({
      latitude: coords.latitude,
      longitude: coords.longitude,
    });
  };

  const handleGeofenceOverride = () => {
    if (!pendingCoordsRef.current || !geofenceOverrideReason.trim()) return;
    checkInMutation.mutate({
      ...pendingCoordsRef.current,
      geofence_override: true,
      geofence_override_reason: geofenceOverrideReason.trim(),
    });
  };

  const handleCheckOut = async () => {
    if (!confirm('Deseja finalizar a visita nesta loja?')) return;

    // Get fresh coords and use return value directly (not stale state)
    let checkoutLat: number | undefined;
    let checkoutLng: number | undefined;
    const coords = await geo.getCurrentPosition();
    if (coords) {
      checkoutLat = coords.latitude;
      checkoutLng = coords.longitude;
    }

    checkOutMutation.mutate({
      latitude: checkoutLat,
      longitude: checkoutLng,
    });
  };

  if (isLoadingStore || isLoadingVisit) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Loja nao encontrada</h2>
        <button
          onClick={() => setLocation('/')}
          className="mt-4 px-6 py-2 bg-primary text-white rounded-lg"
        >
          Voltar ao inicio
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation('/')}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Visita ao PDV</h1>
          </div>
          {/* Tracking badge */}
          {locationTracking.isTracking && (
            <div className="flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-1 text-xs">
              <Crosshair className="h-3.5 w-3.5" />
              <span>{locationTracking.totalDistanceKm.toFixed(1)} km</span>
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-4 space-y-4">
        {/* Store Info Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-3 rounded-full bg-primary/10">
              <MapPin className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">{store.name}</h2>
              <p className="text-sm text-gray-600 mt-1">{store.address}</p>
              <p className="text-sm text-gray-500">{store.city} - {store.state}</p>
            </div>
          </div>

          {store.contact_name && (
            <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
              <Phone className="h-4 w-4 text-gray-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{store.contact_name}</p>
                {store.contact_phone && (
                  <a
                    href={`tel:${store.contact_phone}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {store.contact_phone}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Mini Map with Geofence */}
        {storeHasCoords && (
          <div className="space-y-2">
            <MobileMap
              center={[store.latitude!, store.longitude!]}
              zoom={16}
              userLat={geo.latitude}
              userLng={geo.longitude}
              className="h-[200px] rounded-xl overflow-hidden shadow-sm border border-gray-100"
            >
              {(map) => (
                <GeofenceCircle
                  map={map}
                  lat={store.latitude!}
                  lng={store.longitude!}
                  radiusMeters={geofenceRadius}
                  isInside={geofence.isInside ?? true}
                  storeName={store.name}
                />
              )}
            </MobileMap>

            {/* Distance indicator */}
            {geofence.distanceMeters !== null && (
              <div
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                  geofence.isInside
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                }`}
              >
                <Navigation className="h-4 w-4" />
                {geofence.isInside ? (
                  <span>Dentro da area ({geofence.formattedDistance})</span>
                ) : (
                  <span>A {geofence.formattedDistance} da loja (limite: {geofenceRadius}m)</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Visit Status */}
        {isVisitActive ? (
          <>
            {/* Active Visit Info */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold text-green-900">Visita em Andamento</h3>
                </div>
                {/* Live timer */}
                <div className="flex items-center gap-1.5 bg-green-100 rounded-full px-3 py-1 text-sm font-bold text-green-800">
                  <Clock className="h-4 w-4" />
                  <span>{elapsed}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-green-700">
                <Clock className="h-4 w-4" />
                <span>
                  Check-in: {new Date(activeVisit!.check_in_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              {activeVisit!.perfect_store_score !== undefined && (
                <div className="flex items-center gap-2 mt-2 text-sm text-green-700">
                  <Star className="h-4 w-4" />
                  <span>Score: {activeVisit!.perfect_store_score}%</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => setLocation(`/camera/${activeVisit!.id}`)}
                className="w-full flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50"
              >
                <div className="p-3 rounded-full bg-blue-100">
                  <Camera className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Capturar Fotos</p>
                  <p className="text-sm text-gray-500">Gondola, precos, rupturas</p>
                </div>
              </button>

              <button
                onClick={() => setLocation(`/checklist/${activeVisit!.id}`)}
                className="w-full flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50"
              >
                <div className="p-3 rounded-full bg-purple-100">
                  <ClipboardCheck className="h-6 w-6 text-purple-600" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Checklist</p>
                  <p className="text-sm text-gray-500">Verificar itens de execucao</p>
                </div>
              </button>

              <button
                onClick={() => setLocation(`/prices/${activeVisit!.id}/${storeId}`)}
                className="w-full flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50"
              >
                <div className="p-3 rounded-full bg-green-100">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Coletar Precos</p>
                  <p className="text-sm text-gray-500">Registrar precos competitivos</p>
                </div>
              </button>

              <button
                onClick={() => setLocation(`/extra-point/${activeVisit!.id}/${storeId}`)}
                className="w-full flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50"
              >
                <div className="p-3 rounded-full bg-amber-100">
                  <Award className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Ponto Extra</p>
                  <p className="text-sm text-gray-500">Registrar ponto extra no PDV</p>
                </div>
              </button>

              <button
                onClick={() => setLocation(`/rupture/${activeVisit!.id}/${storeId}`)}
                className="w-full flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50"
              >
                <div className="p-3 rounded-full bg-red-100">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Ruptura</p>
                  <p className="text-sm text-gray-500">Registrar ruptura de produto</p>
                </div>
              </button>

              <button
                onClick={() => setLocation(`/stock-check/${activeVisit!.id}/${storeId}`)}
                className="w-full flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50"
              >
                <div className="p-3 rounded-full bg-teal-100">
                  <Package className="h-6 w-6 text-teal-600" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Verificar Estoque</p>
                  <p className="text-sm text-gray-500">Quantidade, validade e lote</p>
                </div>
              </button>

              <button
                onClick={() => setLocation(`/competitor-collection/${storeId}/${activeVisit!.id}`)}
                className="w-full flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50"
              >
                <div className="p-3 rounded-full bg-orange-100">
                  <Target className="h-6 w-6 text-orange-600" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Coleta Competitiva</p>
                  <p className="text-sm text-gray-500">Precos e promocoes de concorrentes</p>
                </div>
              </button>

              <button
                onClick={() => setLocation(`/materials/${activeVisit!.id}/${storeId}`)}
                className="w-full flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50"
              >
                <div className="p-3 rounded-full bg-indigo-100">
                  <Boxes className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Materiais PDV</p>
                  <p className="text-sm text-gray-500">Verificar materiais de marketing</p>
                </div>
              </button>

              <button
                onClick={() => setLocation(`/planogram/${activeVisit!.id}/${storeId}`)}
                className="w-full flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50"
              >
                <div className="p-3 rounded-full bg-rose-100">
                  <LayoutGrid className="h-6 w-6 text-rose-600" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Planograma</p>
                  <p className="text-sm text-gray-500">Verificar conformidade do planograma</p>
                </div>
              </button>

              <button
                onClick={() => setLocation(`/surveys/${activeVisit!.id}/${storeId}`)}
                className="w-full flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50"
              >
                <div className="p-3 rounded-full bg-sky-100">
                  <ClipboardList className="h-6 w-6 text-sky-600" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Pesquisa PDV</p>
                  <p className="text-sm text-gray-500">Responder pesquisas de sortimento e execução</p>
                </div>
              </button>
            </div>

            {/* Pending Reposition Warning */}
            {unpairedPhotos.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <RefreshCw className="h-5 w-5 text-amber-600" />
                  <h3 className="font-semibold text-amber-900">
                    Reposicao Pendente
                  </h3>
                  <span className="ml-auto bg-amber-200 text-amber-800 text-xs font-bold px-2 py-0.5 rounded-full">
                    {unpairedPhotos.length}
                  </span>
                </div>
                <p className="text-sm text-amber-700 mb-3">
                  {unpairedPhotos.length} ruptura(s) sem foto "Depois". Tire a foto apos repor os produtos.
                </p>
                <div className="space-y-2">
                  {unpairedPhotos.map((photo) => (
                    <div
                      key={photo.id}
                      className="flex items-center gap-3 bg-white rounded-lg p-2 border border-amber-100"
                    >
                      <img
                        src={photo.url}
                        alt="Ruptura"
                        className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {photo.type === 'RUPTURE' ? 'Ruptura' : 'Antes'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(photo.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <button
                        onClick={() => setLocation(`/camera/${activeVisit!.id}?type=AFTER&before_photo_id=${photo.id}`)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium active:bg-amber-600 flex-shrink-0"
                      >
                        <Camera className="h-4 w-4" />
                        Depois
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Check-out Button */}
            {unpairedPhotos.length > 0 && (
              <p className="text-xs text-amber-600 text-center font-medium">
                Existem rupturas sem foto "Depois". O checkout sera registrado com alerta.
              </p>
            )}
            <button
              onClick={handleCheckOut}
              disabled={checkOutMutation.isPending}
              className="w-full flex items-center justify-center gap-2 p-4 bg-red-600 text-white rounded-xl font-semibold active:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <LogOut className="h-5 w-5" />
              {checkOutMutation.isPending ? 'Finalizando...' : 'Finalizar Visita'}
            </button>
          </>
        ) : (
          <>
            {/* Check-in Section */}
            {geo.error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{geo.error}</p>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="text-center mb-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                  <LogIn className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Pronto para iniciar?
                </h3>
                <p className="text-sm text-gray-600">
                  Faca o check-in para comecar a visita a este PDV
                </p>
              </div>

              <button
                onClick={handleCheckIn}
                disabled={geo.isLoading || checkInMutation.isPending}
                className="w-full flex items-center justify-center gap-2 p-4 bg-primary text-white rounded-xl font-semibold active:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {geo.isLoading || checkInMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Obtendo localizacao...</span>
                  </>
                ) : (
                  <>
                    <LogIn className="h-5 w-5" />
                    <span>Fazer Check-in</span>
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-3">
                Sua localizacao sera registrada ao fazer check-in
              </p>
            </div>

            {/* Justify No Visit */}
            <button
              onClick={() => setLocation(`/justify/${storeId}`)}
              className="w-full flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50"
            >
              <div className="p-3 rounded-full bg-orange-100">
                <FileX className="h-6 w-6 text-orange-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-gray-900">Justificar Nao-Visita</p>
                <p className="text-sm text-gray-500">Informar motivo de nao visitar este PDV</p>
              </div>
            </button>
          </>
        )}
      </main>

      {/* Geofence Override Modal */}
      {showGeofenceModal && geofenceError && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setShowGeofenceModal(false);
              setGeofenceError(null);
            }}
          />
          <div className="relative w-full bg-white rounded-t-2xl p-6 animate-slide-up max-h-[90vh] overflow-y-auto">
            <div className="flex justify-center mb-4">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            <div className="text-center mb-4">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Fora da Area</h3>
              <p className="text-sm text-gray-600 mt-1">{geofenceError.message}</p>
            </div>

            {/* Mini map showing distance */}
            {storeHasCoords && geo.latitude && geo.longitude && (
              <MobileMap
                center={[store.latitude!, store.longitude!]}
                zoom={15}
                userLat={geo.latitude}
                userLng={geo.longitude}
                className="h-[180px] rounded-xl overflow-hidden mb-4 border border-gray-200"
              >
                {(map) => (
                  <GeofenceCircle
                    map={map}
                    lat={store.latitude!}
                    lng={store.longitude!}
                    radiusMeters={geofenceError.geofence_radius}
                    isInside={false}
                  />
                )}
              </MobileMap>
            )}

            <div className="bg-red-50 rounded-lg p-3 mb-4 text-sm text-red-700">
              <p>
                Voce esta a <strong>{geofenceError.distance_meters}m</strong> da loja.
                O limite permitido e de <strong>{geofenceError.geofence_radius}m</strong>.
              </p>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Motivo do check-in fora da area *
            </label>
            <textarea
              value={geofenceOverrideReason}
              onChange={(e) => setGeofenceOverrideReason(e.target.value)}
              placeholder="Ex: Estacionamento distante, entrada lateral..."
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 mb-4"
            />

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowGeofenceModal(false);
                  setGeofenceError(null);
                }}
                className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleGeofenceOverride}
                disabled={!geofenceOverrideReason.trim() || checkInMutation.isPending}
                className="flex-1 py-3 bg-amber-500 text-white rounded-xl font-semibold disabled:opacity-50"
              >
                {checkInMutation.isPending ? 'Enviando...' : 'Fazer Check-in'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNps && npsSurvey && completedVisitId && (
        <NpsBottomSheet
          survey={npsSurvey}
          visitId={completedVisitId}
          storeId={storeId}
          onClose={() => {
            setShowNps(false);
            setLocation(`/visit-summary/${completedVisitId}`);
          }}
        />
      )}

      {kmSheetOpen && journeyId && (
        <KmSheet
          journeyId={journeyId}
          type={kmSheetType}
          isOpen={kmSheetOpen}
          onClose={() => {
            setKmSheetOpen(false);
            // After KM end sheet closes, show NPS or redirect
            if (kmSheetType === 'end') {
              if (npsSurvey && completedVisitId) {
                setShowNps(true);
              } else {
                setLocation(`/visit-summary/${completedVisitId}`);
              }
            }
          }}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['journey', 'today'] });
          }}
        />
      )}
    </div>
  );
}
